import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ShieldCheck, HeadphonesIcon, BadgePercent, Sparkles, Star, Search, TrendingDown } from 'lucide-react';
import { popularDestinations, deals, blogPosts, testimonials, trustBadges } from '../mock/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import useTravelpayoutsLoader from '../hooks/useTravelpayoutsLoader';
import CategoryNav, { FLIGHTS_ANCHOR_ID } from '../components/CategoryNav';

const HERO_IMG = 'https://images.pexels.com/photos/14482714/pexels-photo-14482714.jpeg';

// Shared TP WL loader is now imported from ../hooks/useTravelpayoutsLoader.
// Home and Flights both call the same hook — the underlying script is
// injected exactly once and never removed, so SPA navigation between the
// two pages continues to hydrate #tpwl-search / #tpwl-tickets correctly.

// The inline CheaporaCategoryNav has been extracted to
// ../components/CategoryNav.jsx so Hotels and Cars pages can reuse the
// same three-button nav and route the Flights button back to the homepage
// Travelpayouts search section.

// Cheapora's own top-level travel category navigation now lives in
// /components/CategoryNav.jsx so Home, Hotels and Cars share exactly the
// same buttons. The Flights button on non-Home pages navigates to
// /#flights-search which lands on Home and scrolls to the TP WL section.

export default function Home() {
  const location = useLocation();
  // Official Travelpayouts White Label (wl_id=22421) loader (shared hook).
  // TP owns the UI, filters, autocomplete, dates, pax, cabin and search
  // submission inside #tpwl-search / #tpwl-tickets below.
  useTravelpayoutsLoader();

  // When arriving at Home with `#flights-search` in the URL (e.g. clicked
  // the Flights category button from /hotels or /cars), scroll the TP WL
  // section into view once its container has rendered. The scroll is
  // scheduled for the next frame so the browser has laid out the hero.
  useEffect(() => {
    if (location.hash !== `#${FLIGHTS_ANCHOR_ID}`) return;
    const el = document.getElementById(FLIGHTS_ANCHOR_ID);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash, location.key]);

  return (
    <div>
      {/* HERO */}
      <section className="relative hero-wrap">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-8 md:pt-24 md:pb-14">
          <div className="max-w-2xl text-white fade-up">
            <Badge className="bg-amber-400/95 text-slate-900 hover:bg-amber-400 border-0 mb-4">Singapore · STB Licensed TA03795</Badge>
            <h1 className="font-display font-extrabold text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Travel smarter with <span className="text-amber-300">Cheapora</span>
            </h1>
            <p className="mt-4 text-white/90 text-base md:text-lg max-w-xl">
              Compare cheap flights, hotels and cars across hundreds of travel sites at once.
              Book direct with confidence, backed by our Singapore-based local support team.
            </p>
          </div>

          <div className="mt-8 md:mt-10">
            {/* Cheapora's own category nav — kept technically separate from
                the Travelpayouts WL below. Flights is active by default. */}
            <CategoryNav />

            {/* Official Travelpayouts White Label search — replaces the
                previous custom Cheapora SearchWidget on the homepage. The
                loader is injected once via useTravelpayoutsWLLoader().
                TP owns the UI, filters, autocomplete, dates, pax, cabin
                and search submission inside these two containers. The
                outer wrapper carries `id="flights-search"` so the Flights
                category button (and the /#flights-search deep-link) can
                scroll directly to this section. `scroll-mt-24` offsets the
                sticky header so the panel isn't hidden underneath it. */}
            <div
              id={FLIGHTS_ANCHOR_ID}
              className="bg-white/95 backdrop-blur rounded-2xl shadow-xl p-4 md:p-5 scroll-mt-24"
              data-testid="home-tpwl-mount"
            >
              <div id="tpwl-search"></div>
              <div id="tpwl-tickets" className="mt-4"></div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {trustBadges.map(b => (
              <span key={b.label} className="chip bg-white/15 text-white backdrop-blur border border-white/20">
                <ShieldCheck className="w-3.5 h-3.5" /> {b.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* USP */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Search, title: 'Search once, compare everywhere', text: 'We scan hundreds of travel sites so you don’t have to. See the full picture in seconds.' },
            { icon: BadgePercent, title: 'Lowest price promise', text: 'Found a lower fare elsewhere? Tell us within 24h and we’ll match it, guaranteed.' },
            { icon: HeadphonesIcon, title: 'Local Singapore support', text: 'Real humans in Pioneer Centre, available 7 days a week. Not just chatbots.' },
          ].map((f, i) => (
            <Card key={i} className="card-lift border-slate-200">
              <CardContent className="p-6">
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* POPULAR DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-teal-700 font-semibold text-sm tracking-widest uppercase">Popular from Singapore</div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 mt-1">Where Singaporeans are flying</h2>
          </div>
          <Link to="/destinations" className="hidden md:inline-flex items-center gap-1 text-teal-700 font-semibold hover:text-teal-800">
            Explore all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {popularDestinations.map(d => (
            <Link to="/flights" key={d.id} className="group relative overflow-hidden rounded-2xl aspect-[4/5] card-lift">
              <img src={d.image} alt={d.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
              <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                <div className="text-xs uppercase tracking-widest text-white/70">{d.country}</div>
                <div className="font-display font-extrabold text-xl">{d.city}</div>
                <div className="text-xs text-white/80 mt-0.5">{d.tagline}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/70">from</div>
                    <div className="font-display font-extrabold text-lg">S${d.price}</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:bg-amber-400 group-hover:text-slate-900 transition-colors">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DEALS */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-amber-600 font-semibold text-sm tracking-widest uppercase flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Editor-picked deals
            </div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 mt-1">Save up to 32% this month</h2>
          </div>
          <Link to="/deals" className="hidden md:inline-flex items-center gap-1 text-teal-700 font-semibold hover:text-teal-800">
            All deals <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {deals.map(d => (
            <Card key={d.id} className="overflow-hidden border-slate-200 card-lift">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={d.image} alt={d.title} className="absolute inset-0 w-full h-full object-cover" />
                <Badge className="absolute top-3 left-3 bg-amber-500 text-slate-900 border-0">-{d.savings}%</Badge>
                <div className="absolute top-3 right-3 bg-white/95 text-slate-900 text-xs font-semibold px-2 py-1 rounded-full">{d.category}</div>
              </div>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500">{d.location} · {d.nights} nights</div>
                <div className="font-display font-bold text-slate-900 mt-1 line-clamp-1">{d.title}</div>
                <div className="flex items-end justify-between mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">from</div>
                    <div className="font-display font-extrabold text-xl text-teal-700">S${d.price}</div>
                  </div>
                  <Button size="sm" variant="outline" className="border-teal-700 text-teal-700 hover:bg-teal-50">View</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* BAND */}
      <section className="bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-14 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-amber-300 text-xs uppercase tracking-widest font-semibold">Cheapora Concierge</div>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl mt-1">Planning a group trip or honeymoon? Let our team craft it for you.</h3>
            <p className="text-white/85 mt-2 max-w-2xl">Speak to a Singapore-based consultant — no chatbots, no waiting. We’ll design an itinerary within your budget and handle every booking.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/about">
              <Button size="lg" className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-bold rounded-full px-6">Talk to a consultant</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-teal-700 font-semibold text-sm tracking-widest uppercase flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Travel tips
            </div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 mt-1">Stories & guides from our editors</h2>
          </div>
          <Link to="/blog" className="hidden md:inline-flex items-center gap-1 text-teal-700 font-semibold hover:text-teal-800">
            All stories <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {blogPosts.map(b => (
            <Card key={b.id} className="overflow-hidden border-slate-200 card-lift">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={b.image} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <CardContent className="p-4">
                <div className="text-xs text-teal-700 font-semibold uppercase tracking-widest">{b.category}</div>
                <div className="font-display font-bold text-slate-900 mt-1 line-clamp-2">{b.title}</div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{b.excerpt}</p>
                <div className="text-xs text-slate-500 mt-3">{b.date} · {b.readTime} min read</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
          <div className="text-center mb-10">
            <div className="text-teal-700 font-semibold text-sm tracking-widest uppercase">Loved by Singapore travellers</div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 mt-1">4.9 / 5 across 3,240+ reviews</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map(t => (
              <Card key={t.id} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-0.5 text-amber-500 mb-3">
                    {Array.from({length: t.rating}).map((_,i) => <Star key={i} className="w-4 h-4 fill-amber-500" />)}
                  </div>
                  <p className="text-slate-700 leading-relaxed">“{t.text}”</p>
                  <div className="mt-4 text-sm">
                    <div className="font-display font-bold text-slate-900">{t.name}</div>
                    <div className="text-slate-500">{t.location}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
